import type {
  BlackAndWhiteAction,
  BlackAndWhitePlayer,
  BlackAndWhiteState,
  SubmitTileAction,
  ValidationResult,
} from "../types";
import { isTileNumber } from "../utils";

const ok: ValidationResult = { valid: true };

function fail(
  error: string,
  details?: Record<string, unknown>,
): ValidationResult {
  return { valid: false, error, details };
}

function findPlayer(
  state: BlackAndWhiteState,
  playerId: string,
): BlackAndWhitePlayer | undefined {
  return state.players.find((player) => player.playerId === playerId);
}

export function validateSubmitTile(
  state: BlackAndWhiteState,
  action: SubmitTileAction,
): ValidationResult {
  if (state.status !== "IN_PROGRESS" || !state.currentRound) {
    return fail("Game is already completed");
  }

  const player = findPlayer(state, action.playerId);
  if (!player) {
    return fail("Unknown player", { playerId: action.playerId });
  }
  if (!isTileNumber(action.tile)) {
    return fail("Tile must be a number from 1 to 9", { tile: action.tile });
  }
  if (!player.remainingTiles.includes(action.tile)) {
    return fail("Tile has already been used", {
      playerId: action.playerId,
      tile: action.tile,
    });
  }

  if (state.currentRound.phase === "FIRST_SELECTION") {
    if (action.playerId !== state.currentRound.firstPlayerId) {
      return fail("Only the first player can submit now", {
        playerId: action.playerId,
      });
    }
    return ok;
  }

  if (action.playerId !== state.currentRound.secondPlayerId) {
    return fail("Only the second player can submit now", {
      playerId: action.playerId,
    });
  }
  if (!state.currentRound.firstSubmission) {
    return fail("First submission is required before second submission");
  }

  return ok;
}

export function validateAction(
  state: BlackAndWhiteState,
  action: BlackAndWhiteAction,
): ValidationResult {
  switch (action.type) {
    case "SubmitTile":
      return validateSubmitTile(state, action);
  }
}
